import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Inbox, ChevronUp, Loader2, MessageSquarePlus } from 'lucide-react'
import { FeedbackMessage } from '@/hooks/useFeedbackMessages'
import { useSendCollaborationMessageMutation } from '@/redux/collaborationsApi'
import { toast } from '@/components/ui/sonner'

interface FeedbackHubProps {
  campaignId: string
  feedbackMessages: FeedbackMessage[]
  containerInvokeClass?: string
}

const FeedbackHub = ({ campaignId, feedbackMessages, containerInvokeClass }: FeedbackHubProps) => {
  const [composing, setComposing] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [sendMessage, { isLoading: isSending }] = useSendCollaborationMessageMutation()

  const handleSend = async () => {
    if (!messageText.trim()) return
    try {
      await sendMessage({ campaignId, message: messageText.trim() }).unwrap()
      setMessageText('')
      setComposing(false)
      toast('Message sent', { description: 'Your message has been added to the timeline.' })
    } catch {
      toast.error('Failed to send', { description: 'Please try again.' })
    }
  }

  return (
    <Collapsible defaultOpen={true} className={containerInvokeClass}>
      <Card className="bg-gray-900/90 backdrop-blur-xs border border-white/5 rounded-3xl rounded-bl-none">
        <CollapsibleTrigger className="w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-center gap-3">
              <Inbox className="h-5 w-5 text-white" />
              <CardTitle className="text-white">Feedback Hub</CardTitle>
            </div>
            <ChevronUp className="h-5 w-5 text-white" />
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {feedbackMessages.length === 0 ? (
              <div className="text-center py-6 text-gray-400">
                <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No feedback yet</p>
                <p className="text-xs mt-1">Feedback from the brand will appear here</p>
              </div>
            ) : (
              feedbackMessages.map((msg) => (
                <div key={msg.id} className="border-b border-gray-800 pb-4 last:border-0">
                  {msg.date && (
                    <div className="text-xs text-gray-500 text-center mb-4">{msg.date}</div>
                  )}
                  <div className="flex gap-3">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={msg.avatar || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {msg.sender.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{msg.sender}</span>
                        <span className="text-xs text-gray-400">{msg.timestamp}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-gray-300">{msg.type}</span>
                        {msg.status === 'needs_revisions' && (
                          <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs">
                            Needs Revisions
                          </Badge>
                        )}
                        {msg.status === 'approved' && (
                          <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs">
                            Approved
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm">{msg.message}</p>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Compose area */}
            {composing ? (
              <div className="border-t border-gray-800 pt-4 space-y-3">
                <Textarea
                  placeholder="Write a message to the brand..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  rows={3}
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setComposing(false)
                      setMessageText('')
                    }}
                    className="border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="lime"
                    disabled={isSending || !messageText.trim()}
                    onClick={handleSend}
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-t border-gray-800 pt-4">
                <Button
                  variant="outline"
                  className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                  onClick={() => setComposing(true)}
                >
                  <MessageSquarePlus className="mr-2 h-4 w-4" />
                  Message Brand
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

export default FeedbackHub
